/**
 * Settings → Locations & scanners (design-reference §4.10).
 *
 * An "unmapped devices" callout (GET /v1/scanners/unmapped) with a map-it menu,
 * the location hierarchy as a real tree (GET /v1/locations, nested by parent_id)
 * with inline add/rename, and the scanners table (GET /v1/scanners) with inline
 * edit of model + mapped location. Scanner counts per location are derived from
 * the real scanners list; nothing is invented.
 */
import { useCallback, useMemo, useRef, useState } from 'react';

import type { Location, Scanner } from '../../api/schema';
import { Button, EmptyState, ErrorState, Modal, Select, Skeleton, useToast } from '../../components';
import { useOnClickOutside } from '../../lib/hooks';
import { useLoadState } from '../../lib/useLoadState';
import { useSettingsApi } from './settingsApi';
import { reasonOf } from './format';
import './settings.css';

interface LocationsData {
  locations: Location[];
  scanners: Scanner[];
  unmapped: string[];
}

interface TreeNode {
  location: Location;
  depth: number;
}

/** Flatten the location list into DFS order with a depth for each row. */
function toTree(locations: readonly Location[]): TreeNode[] {
  const byParent = new Map<string | null, Location[]>();
  for (const loc of locations) {
    const parent = loc.parent_id ?? null;
    const list = byParent.get(parent) ?? [];
    list.push(loc);
    byParent.set(parent, list);
  }
  for (const list of byParent.values()) {
    list.sort((a, b) => a.name.localeCompare(b.name));
  }
  const out: TreeNode[] = [];
  const walk = (parent: string | null, depth: number): void => {
    for (const loc of byParent.get(parent) ?? []) {
      out.push({ location: loc, depth });
      walk(loc.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

export function LocationsScreen(): React.JSX.Element {
  const { toast } = useToast();
  const settingsApi = useSettingsApi();
  const [dismissedUnmapped, setDismissedUnmapped] = useState<Set<string>>(new Set());

  // Location add/edit.
  const [locForm, setLocForm] = useState<{ mode: 'add' | 'edit'; id?: string; name: string; parent: string } | null>(
    null,
  );
  const [locBusy, setLocBusy] = useState(false);

  // Scanner add/edit. `device_id` is only editable while adding — it is the
  // tenant-unique key a device quotes on every submission, so renaming it after
  // the fact would orphan every document already filed against it.
  const [scanForm, setScanForm] = useState<
    | { mode: 'add'; deviceId: string; model: string; location: string }
    | { mode: 'edit'; id: string; deviceId: string; model: string; location: string }
    | null
  >(null);
  const [scanBusy, setScanBusy] = useState(false);

  const fetchLocations = useCallback(
    async (signal: AbortSignal): Promise<LocationsData> => {
      const [locations, scanners, unmapped] = await Promise.all([
        settingsApi.locations.list(signal),
        settingsApi.scanners.list(signal),
        settingsApi.scanners.unmapped(signal),
      ]);
      return { locations: locations.items, scanners: scanners.items, unmapped };
    },
    [settingsApi],
  );
  const state = useLoadState(fetchLocations);
  const reload = state.status === 'ready' || state.status === 'error' ? state.reload : undefined;

  const locationName = useCallback(
    (id: string | null | undefined): string => {
      if (state.status !== 'ready' || id === null || id === undefined) return 'Unmapped';
      return state.data.locations.find((l) => l.id === id)?.name ?? 'Unmapped';
    },
    [state],
  );

  const scannerCount = useMemo(() => {
    const counts = new Map<string, number>();
    if (state.status === 'ready') {
      for (const s of state.data.scanners) {
        if (s.location_id != null) counts.set(s.location_id, (counts.get(s.location_id) ?? 0) + 1);
      }
    }
    return counts;
  }, [state]);

  const mapDevice = useCallback(
    (deviceId: string, locationId: string) => {
      void (async () => {
        try {
          await settingsApi.scanners.create({ device_id: deviceId, location_id: locationId });
          setDismissedUnmapped((prev) => new Set(prev).add(deviceId));
          toast(`${deviceId} mapped to ${locationName(locationId)} — analytics will backfill`);
          reload?.();
        } catch {
          toast('Could not map the device — try again', { tone: 'error' });
        }
      })();
    },
    [locationName, toast, reload, settingsApi],
  );

  const submitLoc = useCallback(() => {
    const form = locForm;
    if (form === null || form.name.trim() === '') return;
    setLocBusy(true);
    void (async () => {
      try {
        const parentId = form.parent === '' ? null : form.parent;
        if (form.mode === 'add') {
          await settingsApi.locations.create({ name: form.name.trim(), parent_id: parentId });
          toast('Location added');
        } else if (form.id !== undefined) {
          await settingsApi.locations.update(form.id, { name: form.name.trim(), parent_id: parentId });
          toast('Location renamed');
        }
        setLocForm(null);
        reload?.();
      } catch {
        toast('Could not save the location — try again', { tone: 'error' });
      } finally {
        setLocBusy(false);
      }
    })();
  }, [locForm, toast, reload, settingsApi]);

  const submitScanner = useCallback(() => {
    const form = scanForm;
    if (form === null) return;
    if (form.mode === 'add' && form.deviceId.trim() === '') return;
    setScanBusy(true);
    void (async () => {
      const model = form.model.trim() === '' ? null : form.model.trim();
      const locationId = form.location === '' ? null : form.location;
      try {
        if (form.mode === 'add') {
          await settingsApi.scanners.create({
            device_id: form.deviceId.trim(),
            model,
            location_id: locationId,
          });
          toast('Scanner registered — documents already carrying this device ID will be linked to it');
        } else {
          await settingsApi.scanners.update(form.id, { model, location_id: locationId });
          toast('Scanner updated');
        }
        setScanForm(null);
        reload?.();
      } catch (error) {
        // A duplicate device_id comes back as a 409 naming the clash; show that
        // rather than a generic failure the user cannot act on.
        const fallback =
          form.mode === 'add'
            ? 'Could not register the scanner — try again'
            : 'Could not update the scanner — try again';
        toast(reasonOf(error, fallback), { tone: 'error' });
      } finally {
        setScanBusy(false);
      }
    })();
  }, [scanForm, toast, reload, settingsApi]);

  const openAddScanner = useCallback(() => {
    setScanForm({ mode: 'add', deviceId: '', model: '', location: '' });
  }, []);

  if (state.status === 'loading') {
    return (
      <section className="set-wrap" aria-label="Locations & scanners">
        <Skeleton height={44} />
        <div className="set-two set-two--5-7">
          <Skeleton height={260} />
          <Skeleton height={260} />
        </div>
        <p className="set-loading-caption">Loading locations and scanners…</p>
      </section>
    );
  }

  if (state.status === 'error') {
    return (
      <section className="set-wrap" aria-label="Locations & scanners">
        <ErrorState
          title="We couldn't load your locations"
          body="The settings service didn't respond. Try again in a moment."
          traceId={state.traceId ?? undefined}
          actions={
            <Button onClick={state.reload} variant="primary">
              Retry
            </Button>
          }
        />
      </section>
    );
  }

  const { locations, scanners, unmapped } = state.data;
  const tree = toTree(locations);
  const activeUnmapped = unmapped.filter((d) => !dismissedUnmapped.has(d));
  const parentOptions = [
    { value: '', label: 'No parent (top level)' },
    ...locations.map((l) => ({ value: l.id, label: l.name })),
  ];
  const locationOptions = [{ value: '', label: 'Unmapped' }, ...locations.map((l) => ({ value: l.id, label: l.name }))];

  return (
    <section className="set-wrap" aria-label="Locations & scanners">
      {activeUnmapped.map((device) => (
        <div className="set-banner" role="status" key={device}>
          <span className="set-dot set-dot--warn" aria-hidden="true" />
          <span>
            <span className="set-mono">{device}</span> appeared in ingested metadata but isn&apos;t mapped to a
            location — its documents show as &quot;Unmapped&quot; in analytics.
          </span>
          <span className="set-banner__spacer" />
          <MapItMenu
            device={device}
            locations={locations}
            onPick={(locId) => { mapDevice(device, locId); }}
          />
        </div>
      ))}

      <div className="set-two set-two--5-7">
        {/* Locations tree ---------------------------------------------------- */}
        <div className="set-card">
          <div className="set-card__head">
            <h2 className="set-card__title">Locations</h2>
            <span className="set-card__spacer" />
            <Button
              variant="secondary"
              size="sm"
              onClick={() => { setLocForm({ mode: 'add', name: '', parent: '' }); }}
            >
              + Add
            </Button>
          </div>
          {tree.length === 0 ? (
            <EmptyState
              title="Name your first location"
              body="Add a site, then departments and rooms beneath it. Friendly names replace raw device IDs everywhere in analytics."
              actions={
                <Button variant="primary" onClick={() => { setLocForm({ mode: 'add', name: '', parent: '' }); }}>
                  Add a location
                </Button>
              }
            />
          ) : (
            <ul className="set-tree" aria-label="Location hierarchy">
              {tree.map(({ location, depth }) => {
                const count = scannerCount.get(location.id) ?? 0;
                const weight = depth === 0 ? 600 : depth === 1 ? 500 : 400;
                const color = depth >= 2 ? 'var(--t2)' : 'var(--t1)';
                return (
                  <li
                    className="set-tree__row"
                    key={location.id}
                    style={{ paddingLeft: 14 + depth * 22, paddingRight: 18, paddingTop: 8, paddingBottom: 8 }}
                  >
                    <button
                      type="button"
                      className="set-linkbtn"
                      style={{ fontWeight: weight, color, fontSize: '12.5px' }}
                      onClick={() => {
                        setLocForm({ mode: 'edit', id: location.id, name: location.name, parent: location.parent_id ?? '' });
                      }}
                    >
                      {location.name}
                    </button>
                    {count > 0 ? (
                      <span className="set-tree__meta">
                        {count} scanner{count === 1 ? '' : 's'}
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
          <p className="set-card__foot">
            Hierarchy: site → department → room. Friendly names replace raw IDs everywhere in analytics.
          </p>
        </div>

        {/* Scanners table --------------------------------------------------- */}
        <div className="set-card">
          <div className="set-card__head">
            <h2 className="set-card__title">Scanners</h2>
            <span className="set-card__aside set-card__spacer">
              {scanners.filter((s) => s.location_id != null).length} mapped device
              {scanners.filter((s) => s.location_id != null).length === 1 ? '' : 's'}
            </span>
            <Button variant="secondary" size="sm" onClick={openAddScanner}>
              + Add scanner
            </Button>
          </div>
          {scanners.length === 0 ? (
            <EmptyState
              title="Register your first scanner"
              body="Add the device ID the scanner sends with each document, then map it to a location. Devices that submit before they are registered appear above as unmapped."
              actions={
                <Button variant="primary" onClick={openAddScanner}>
                  Add a scanner
                </Button>
              }
            />
          ) : (
            <div className="set-scroll">
              <div
                className="set-grid set-grid__head"
                role="row"
                style={{ gridTemplateColumns: '1fr 1.6fr 1.3fr 60px' }}
              >
                <span role="columnheader">Device ID</span>
                <span role="columnheader">Model</span>
                <span role="columnheader">Location</span>
                <span role="columnheader" className="sr-only">
                  Actions
                </span>
              </div>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} aria-label="Scanners">
                {scanners.map((s) => (
                  <li
                    className="set-grid set-grid__row"
                    key={s.id}
                    style={{ gridTemplateColumns: '1fr 1.6fr 1.3fr 60px' }}
                  >
                    <span className="set-mono" style={{ fontSize: 11.5 }}>
                      {s.device_id}
                    </span>
                    <span style={{ color: 'var(--t2)' }}>{s.model ?? '—'}</span>
                    <span style={{ color: 'var(--t2)' }}>{locationName(s.location_id)}</span>
                    <span style={{ textAlign: 'right' }}>
                      <button
                        type="button"
                        className="set-linkbtn"
                        onClick={() => {
                          setScanForm({
                            mode: 'edit',
                            id: s.id,
                            deviceId: s.device_id,
                            model: s.model ?? '',
                            location: s.location_id ?? '',
                          });
                        }}
                      >
                        Edit
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Location add/edit modal ------------------------------------------- */}
      <Modal
        open={locForm !== null}
        onClose={() => { setLocForm(null); }}
        title={locForm?.mode === 'edit' ? 'Rename location' : 'Add location'}
        dismissible
        footer={
          <div className="ds-modal__footer">
            <Button variant="secondary" onClick={() => { setLocForm(null); }}>
              Cancel
            </Button>
            <Button variant="primary" loading={locBusy} disabled={(locForm?.name ?? '').trim() === ''} onClick={submitLoc}>
              {locForm?.mode === 'edit' ? 'Save' : 'Add location'}
            </Button>
          </div>
        }
      >
        {locForm !== null ? (
          <div className="ds-modal__body">
            <div className="ds-field">
              <label className="ds-field__label" htmlFor="loc-name">
                Name
              </label>
              <input
                id="loc-name"
                className="ds-input"
                placeholder="e.g. Midtown Branch"
                value={locForm.name}
                onChange={(e) => { setLocForm({ ...locForm, name: e.currentTarget.value }); }}
              />
            </div>
            <div className="ds-field" style={{ marginTop: 12 }}>
              <label className="ds-field__label" htmlFor="loc-parent">
                Parent location
              </label>
              <Select
                id="loc-parent"
                options={parentOptions.filter((o) => o.value !== locForm.id)}
                value={locForm.parent}
                onChange={(e) => { setLocForm({ ...locForm, parent: e.currentTarget.value }); }}
              />
            </div>
          </div>
        ) : null}
      </Modal>

      {/* Scanner add/edit modal -------------------------------------------- */}
      <Modal
        open={scanForm !== null}
        onClose={() => { setScanForm(null); }}
        title={scanForm?.mode === 'add' ? 'Add scanner' : 'Edit scanner'}
        dismissible
        footer={
          <div className="ds-modal__footer">
            <Button variant="secondary" onClick={() => { setScanForm(null); }}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={scanBusy}
              disabled={scanForm?.mode === 'add' && scanForm.deviceId.trim() === ''}
              onClick={submitScanner}
            >
              {scanForm?.mode === 'add' ? 'Add scanner' : 'Save'}
            </Button>
          </div>
        }
      >
        {scanForm !== null ? (
          <div className="ds-modal__body">
            <div className="ds-field">
              <label className="ds-field__label" htmlFor="scan-device">
                Device ID
              </label>
              {scanForm.mode === 'add' ? (
                <>
                  <input
                    id="scan-device"
                    className="ds-input"
                    placeholder="e.g. MFP-004"
                    value={scanForm.deviceId}
                    onChange={(e) => { setScanForm({ ...scanForm, deviceId: e.currentTarget.value }); }}
                  />
                  <span className="ds-field__hint">
                    The identifier this device sends with every document. Must be unique in your tenant and match
                    exactly — it is what links a submission to this scanner.
                  </span>
                </>
              ) : (
                <>
                  <input id="scan-device" className="ds-input set-mono" value={scanForm.deviceId} readOnly disabled />
                  <span className="ds-field__hint">
                    A device ID cannot be changed — documents are already filed against it.
                  </span>
                </>
              )}
            </div>
            <div className="ds-field" style={{ marginTop: 12 }}>
              <label className="ds-field__label" htmlFor="scan-model">
                Model
              </label>
              <input
                id="scan-model"
                className="ds-input"
                placeholder="e.g. Canon iR-ADV C5560"
                value={scanForm.model}
                onChange={(e) => { setScanForm({ ...scanForm, model: e.currentTarget.value }); }}
              />
            </div>
            <div className="ds-field" style={{ marginTop: 12 }}>
              <label className="ds-field__label" htmlFor="scan-loc">
                Mapped location
              </label>
              <Select
                id="scan-loc"
                options={locationOptions}
                value={scanForm.location}
                onChange={(e) => { setScanForm({ ...scanForm, location: e.currentTarget.value }); }}
              />
            </div>
          </div>
        ) : null}
      </Modal>
    </section>
  );
}

/** The "Map it ▾" menu on the unmapped-device callout. */
function MapItMenu({
  device,
  locations,
  onPick,
}: {
  device: string;
  locations: readonly Location[];
  onPick: (locationId: string) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, open, () => { setOpen(false); });

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <Button
        variant="primary"
        size="sm"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => { setOpen((o) => !o); }}
      >
        Map it ▾
      </Button>
      {open ? (
        <ul className="ds-menu" role="menu" aria-label={`Map ${device} to a location`} style={{ right: 0, width: 190 }}>
          {locations.length === 0 ? (
            <li>
              <span className="ds-menu__item" style={{ color: 'var(--t3)' }}>
                Add a location first
              </span>
            </li>
          ) : (
            locations.map((loc) => (
              <li key={loc.id}>
                <button
                  type="button"
                  role="menuitem"
                  className="ds-menu__item"
                  onClick={() => {
                    onPick(loc.id);
                    setOpen(false);
                  }}
                >
                  {loc.name}
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}
